import { alertDialog } from "@serenity-is/corelib";

export interface IdleTimeoutOptions {
    activityTimeout: number;  // Time in seconds before showing warning
    warningDuration: number;  // Time in seconds to show warning before signout
    signoutUrl: string;       // URL to redirect for signout
}

export class IdleTimeoutHandler {
    private options: IdleTimeoutOptions;
    private lastActivity: number;
    private warningTimer: number | null = null;
    private checkTimer: number | null = null;
    private warningShown: boolean = false;

    constructor(options: IdleTimeoutOptions) {
        this.options = options;
        this.lastActivity = Date.now();
        this.setupActivityListeners();
        this.startMonitoring();
    }

    private setupActivityListeners(): void {
        // Track user activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        events.forEach(event => {
            document.addEventListener(event, () => this.updateActivity(), true);
        });
    }

    private updateActivity(): void {
        this.lastActivity = Date.now();
        this.warningShown = false;
        
        // Clear warning timer if activity detected
        if (this.warningTimer) {
            window.clearTimeout(this.warningTimer);
            this.warningTimer = null;
        }
    }

    private startMonitoring(): void {
        this.checkTimer = window.setInterval(() => {
            const idleTime = (Date.now() - this.lastActivity) / 1000;
            
            // Check if we should show warning
            if (idleTime >= this.options.activityTimeout && !this.warningShown) {
                this.showWarning();
            }
        }, 5000); // Check every 5 seconds
    }

    private showWarning(): void {
        this.warningShown = true;
        const remainingSeconds = this.options.warningDuration;
        
        alertDialog(
            `You will be signed out in ${remainingSeconds} seconds due to inactivity. ` +
            `Please move your mouse or press a key to stay logged in.`,
            {
                onClose: () => {
                    // Dialog closed, refresh activity
                    this.updateActivity();
                }
            }
        );

        // Set timer to sign out after warning duration
        this.warningTimer = window.setTimeout(() => {
            this.signOut();
        }, this.options.warningDuration * 1000);
    }

    private signOut(): void {
        if (this.checkTimer) {
            window.clearInterval(this.checkTimer);
        }
        if (this.warningTimer) {
            window.clearTimeout(this.warningTimer);
        }
        
        // Redirect to signout URL
        window.location.href = this.options.signoutUrl;
    }

    public destroy(): void {
        if (this.checkTimer) {
            window.clearInterval(this.checkTimer);
        }
        if (this.warningTimer) {
            window.clearTimeout(this.warningTimer);
        }
    }
}
